import React, { createContext, useContext, useMemo } from 'react'

import { CanvasFocus, CanvasScope } from './types'

const canvasScopeContext = createContext<CanvasScope | undefined>(undefined)

interface CanvasScopeProviderProps {
  /**
   * The page on screen. Passed in rather than read from the route because only
   * the route-level component knows how to resolve it: the page and Section
   * routes carry it in the URL, the Share frame route
   * (`/s/:shareID/f/:frameUUID`) does not and has to look it up from the frame,
   * and the root Preview route falls back to the Preview's first page.
   */
  pageUUID: string
  /**
   * What the canvas is focused on, from `useCanvasFocus`. Passed in for the
   * same reason as the page: the caller has to know it first, because on the
   * Share frame route the page is looked up from the focused frame.
   */
  focus: CanvasFocus
  children: React.ReactNode
}

/**
 * Publishes what the canvas is showing.
 *
 * Both halves are passed in rather than read from the route here, so this stays
 * a plain publisher and the derivation happens once per host, above it.
 *
 * Before this, the same question was answered in four places and four ways -
 * route matching in `useCopyLinkShortcut`, `match.params` threaded down as
 * props from the route-level queries, and the renderer's own single-entity
 * state read back in `SharePageCanvas` - each under a different name.
 */
export function CanvasScopeProvider({
  pageUUID,
  focus,
  children,
}: CanvasScopeProviderProps) {
  const scope = useMemo(
    (): CanvasScope => ({
      pageUUID,
      focus,
      entityUUID: focus.kind === 'page' ? undefined : focus.uuid,
    }),
    [focus, pageUUID]
  )

  return (
    <canvasScopeContext.Provider value={scope}>
      {children}
    </canvasScopeContext.Provider>
  )
}

/**
 * What the canvas is showing, or undefined outside a canvas view.
 *
 * Optional so that consumers shared with non-canvas surfaces keep working
 * without one; each falls back to the behaviour it had before the scope
 * existed.
 */
export function useCanvasScope(): CanvasScope | undefined {
  return useContext(canvasScopeContext)
}
